/**
 * PHARMAKON — Fournisseur ERP · sidebar navigation config (Module 00)
 *
 * Single source of truth for the supplier portal left-nav: nav item → route +
 * lucide icon + optional module gate + optional capability gate + badge key.
 * Implementation_Plan §3.5.1 / Appendix E.1.
 *
 * `module` (when set) gates the item behind a subscribed module — gated items
 * still render (with a lock affordance) and route to a real-but-limited preview.
 * `capability` (when set) hides the item unless the staff role permits it.
 * `badge` keys map to counts surfaced by the portal store.
 */

export const FOURNISSEUR_NAV = Object.freeze([
  {
    label: "Pilotage",
    items: [
      {
        id: "dashboard",
        label: "Tableau de bord",
        icon: "layout-dashboard",
        to: "/portail-fournisseur/dashboard",
      },
    ],
  },
  {
    label: "Facturation",
    items: [
      {
        id: "efactures",
        label: "E-Factures",
        icon: "file-text",
        to: "/portail-fournisseur/efactures",
      },
    ],
  },
  {
    label: "Approvisionnement",
    items: [
      {
        id: "approv-commandes",
        label: "Bons de commande",
        icon: "clipboard-list",
        to: "/portail-fournisseur/approvisionnement/commandes",
      },
      {
        id: "approv-fabricants",
        label: "Fabricants",
        icon: "factory",
        to: "/portail-fournisseur/approvisionnement/fabricants",
      },
      {
        id: "approv-couts",
        label: "Coûts d'achat",
        icon: "receipt-text",
        to: "/portail-fournisseur/approvisionnement/couts",
      },
    ],
  },
  {
    label: "Commerce",
    items: [
      {
        id: "catalogue",
        label: "Catalogue",
        icon: "package",
        to: "/portail-fournisseur/catalogue",
        end: true, // exact-match: don't stay active on /catalogue/ppa
        module: "CATALOGUE_FOURNISSEUR",
      },
      {
        id: "catalogue-ppa",
        label: "Références PPA",
        icon: "landmark",
        to: "/portail-fournisseur/catalogue/ppa",
        module: "CATALOGUE_FOURNISSEUR",
      },
      {
        id: "connexions",
        label: "Connexions",
        icon: "link-2",
        to: "/portail-fournisseur/connexions",
      },
      {
        id: "commercial-precommandes",
        label: "Pré-commandes",
        icon: "shopping-cart",
        to: "/portail-fournisseur/commercial/pre-commandes",
        badge: "precommandes",
        module: "PRECOMMANDES",
      },
      {
        id: "commercial-bons-commande",
        label: "Bons de commande",
        icon: "file-check",
        to: "/portail-fournisseur/commercial/bons-commande",
      },
      // Refonte WP-36 (A-20, ux/01 §6.10, ux/03 §5.8, OP-F-25): the one entry the portal gains — after
      // « Bons de commande », badge = réclamations to answer or unread; not module-gated (OP-03-31).
      {
        id: "reclamations-recues",
        label: "Réclamations reçues",
        icon: "message-square-warning",
        to: "/portail-fournisseur/reclamations",
        badge: "reclamations",
      },
      {
        id: "commercial-promotions",
        label: "Promotions",
        icon: "tag",
        to: "/portail-fournisseur/commercial/promotions",
        module: "TARIFICATION_AVANCEE",
      },
      {
        id: "clients",
        label: "Clients",
        // AUD-RECL-18 (WP-36): lucide-react 0.323 has no « hospital » — the entry rendered without icon
        icon: "store",
        to: "/portail-fournisseur/clients",
      },
    ],
  },
  {
    label: "Logistique",
    items: [
      {
        id: "entrepots",
        label: "Entrepôts",
        icon: "warehouse",
        to: "/portail-fournisseur/entrepots",
        end: true, // exact-match: don't stay active on /entrepots/stock etc.
      },
      {
        id: "entrepots-stock",
        label: "Stock par lot",
        icon: "boxes",
        to: "/portail-fournisseur/entrepots/stock",
      },
      {
        id: "entrepots-mouvements",
        label: "Mouvements",
        icon: "history",
        to: "/portail-fournisseur/entrepots/mouvements",
      },
      {
        id: "entrepots-transferts",
        label: "Transferts",
        icon: "arrow-left-right",
        to: "/portail-fournisseur/entrepots/transferts",
      },
    ],
  },
  {
    // Module 05 — Logistics & Delivery. BL + cold-chain + recall trace are free
    // core (no module gate); colis/tournée/suivi carry the LOGISTIQUE_PRO lock
    // affordance and the fleet the PARC_VEHICULES lock — each routes to a
    // real-but-limited preview, never a dead page.
    label: "Livraisons",
    items: [
      {
        id: "logistique-livraisons",
        label: "Bons de livraison",
        icon: "truck",
        to: "/portail-fournisseur/logistique/livraisons",
      },
      {
        id: "logistique-colis",
        label: "Colis",
        icon: "package",
        to: "/portail-fournisseur/logistique/colis",
        end: true, // exact-match: don't stay active on /colis/:id
        module: "LOGISTIQUE_PRO",
      },
      {
        id: "logistique-tournees",
        label: "Tournées",
        icon: "navigation",
        to: "/portail-fournisseur/logistique/tournees",
        module: "LOGISTIQUE_PRO",
      },
      {
        id: "logistique-suivi",
        label: "Suivi",
        icon: "route",
        to: "/portail-fournisseur/logistique/suivi",
        module: "LOGISTIQUE_PRO",
      },
      {
        id: "logistique-chaine-froid",
        label: "Chaîne du froid",
        icon: "snowflake",
        to: "/portail-fournisseur/logistique/chaine-froid",
      },
      {
        id: "logistique-flotte",
        label: "Parc & chauffeurs",
        icon: "car-front",
        to: "/portail-fournisseur/logistique/flotte",
        module: "PARC_VEHICULES",
      },
      {
        id: "logistique-rappels",
        label: "Traçage rappels",
        icon: "siren",
        to: "/portail-fournisseur/logistique/rappels",
      },
      {
        id: "logistique-chauffeur",
        label: "Application chauffeur",
        icon: "smartphone",
        to: "/portail-fournisseur/logistique/chauffeur",
        module: "LOGISTIQUE_PRO",
      },
    ],
  },
  {
    // Module 06 — Finance & Settlement. Receivables / payments / échéancier /
    // avoir / trésorerie / credit control are free core (no module gate); the
    // Comptabilité workspace carries the COMPTABILITE_PRO lock affordance and
    // routes to a real-but-limited preview, never a dead page.
    label: "Finance",
    items: [
      {
        id: "finance-creances",
        label: "Créances",
        icon: "circle-dollar-sign",
        to: "/portail-fournisseur/finance/creances",
      },
      {
        id: "finance-paiements",
        label: "Paiements",
        icon: "banknote",
        to: "/portail-fournisseur/finance/paiements",
        end: true, // exact-match: don't stay active on /paiements/nouveau
      },
      {
        id: "finance-echeancier",
        label: "Échéancier",
        icon: "calendar-clock",
        to: "/portail-fournisseur/finance/echeancier",
      },
      {
        id: "finance-avoirs",
        label: "Avoirs",
        icon: "file-minus",
        to: "/portail-fournisseur/finance/avoirs",
      },
      {
        id: "finance-tresorerie",
        label: "Trésorerie",
        icon: "wallet",
        to: "/portail-fournisseur/finance/tresorerie",
      },
      {
        id: "finance-credit",
        label: "Contrôle de crédit",
        icon: "shield-check",
        to: "/portail-fournisseur/finance/credit",
      },
      {
        id: "finance-releves",
        label: "Relevés de compte",
        icon: "receipt-text",
        to: "/portail-fournisseur/finance/releves",
      },
      {
        id: "finance-relances",
        label: "Relances",
        icon: "bell-ring",
        to: "/portail-fournisseur/finance/relances",
      },
      {
        id: "finance-payables",
        label: "Fournisseurs à payer",
        icon: "file-input",
        to: "/portail-fournisseur/finance/payables",
      },
      {
        id: "finance-cheques",
        label: "Chèques",
        icon: "scroll-text",
        to: "/portail-fournisseur/finance/cheques",
      },
      {
        id: "finance-rapprochement",
        label: "Rapprochement",
        icon: "landmark",
        to: "/portail-fournisseur/finance/rapprochement",
      },
      {
        id: "finance-comptabilite",
        label: "Comptabilité",
        icon: "book-open",
        to: "/portail-fournisseur/finance/comptabilite",
        module: "COMPTABILITE_PRO",
      },
      {
        id: "finance-declarations",
        label: "Déclarations (G50)",
        icon: "scale",
        to: "/portail-fournisseur/finance/declarations",
        module: "COMPTABILITE_PRO",
      },
    ],
  },
  {
    // Module 07 — Analytics & Rotation. The basic Dashboard + the partial-free
    // Rotation are free core (no module gate); the advanced BI (Cockpit/E2,
    // Rapports/E3, Rentabilité/E6, Radar/E1) carry the ANALYTICS_AVANCE lock
    // affordance and the forecast-derived surfaces (Prévisions, Back-testing/E4,
    // Simulation/E5) the PREVISION_STOCK lock — each routes to a real-but-limited
    // preview, never a dead page.
    label: "Analytique",
    items: [
      {
        id: "analytics-dashboard",
        label: "Tableau de bord",
        icon: "bar-chart-3",
        to: "/portail-fournisseur/analytics/tableau-de-bord",
      },
      {
        id: "analytics-rotation",
        label: "État de rotation",
        icon: "rotate-cw",
        to: "/portail-fournisseur/analytics/rotation",
      },
      {
        id: "analytics-previsions",
        label: "Prévisions",
        icon: "trending-up",
        to: "/portail-fournisseur/analytics/previsions",
        end: true, // exact-match: don't stay active on /previsions/backtest etc.
        module: "PREVISION_STOCK",
      },
      {
        id: "analytics-backtest",
        label: "Back-testing",
        icon: "target",
        to: "/portail-fournisseur/analytics/previsions/backtest",
        module: "PREVISION_STOCK",
      },
      {
        id: "analytics-simulation",
        label: "Simulation what-if",
        icon: "sliders-horizontal",
        to: "/portail-fournisseur/analytics/previsions/simulation",
        module: "PREVISION_STOCK",
      },
      {
        id: "analytics-radar",
        label: "Radar",
        icon: "radar",
        to: "/portail-fournisseur/analytics/radar",
        module: "ANALYTICS_AVANCE",
      },
      {
        id: "analytics-cockpit",
        label: "Cockpit KPI",
        icon: "gauge",
        to: "/portail-fournisseur/analytics/cockpit",
        module: "ANALYTICS_AVANCE",
      },
      {
        id: "analytics-rapports",
        label: "Rapports",
        icon: "file-spreadsheet",
        to: "/portail-fournisseur/analytics/rapports",
        module: "ANALYTICS_AVANCE",
      },
      {
        id: "analytics-profitabilite",
        label: "Rentabilité",
        icon: "scale",
        to: "/portail-fournisseur/analytics/profitabilite",
        module: "ANALYTICS_AVANCE",
      },
      {
        id: "analytics-conformite",
        label: "Conformité",
        icon: "shield-check",
        to: "/portail-fournisseur/analytics/conformite",
      },
    ],
  },
  {
    // Module 08 — Subscription & Monetization. The billing portal is visible
    // to ALL supplier staff (read = fournisseur_subscription.read); manage
    // actions (souscrire / annuler / addon / essai…) gate on
    // fournisseur_subscription.manage INSIDE the pages, never in the nav.
    label: "Abonnement",
    items: [
      {
        id: "abonnement",
        label: "Mon abonnement",
        icon: "credit-card",
        to: "/portail-fournisseur/abonnement",
        end: true, // exact-match: don't stay active on /abonnement/catalogue
      },
      {
        id: "abonnement-catalogue",
        label: "Catalogue & plans",
        icon: "shopping-bag",
        to: "/portail-fournisseur/abonnement/catalogue",
      },
      {
        id: "abonnement-historique",
        label: "Paiements",
        icon: "receipt",
        to: "/portail-fournisseur/abonnement/historique",
      },
      {
        id: "abonnement-parrainage",
        label: "Parrainage",
        icon: "gift",
        to: "/portail-fournisseur/abonnement/parrainage",
      },
    ],
  },
  {
    label: "Organisation",
    items: [
      {
        id: "equipe",
        label: "Équipe",
        icon: "users",
        to: "/portail-fournisseur/equipe",
        capability: "fournisseur_staff.manage",
      },
      {
        id: "parametres",
        label: "Notifications",
        icon: "sliders-horizontal",
        to: "/portail-fournisseur/parametres/notifications",
      },
    ],
  },
]);

export default FOURNISSEUR_NAV;
